import {IReg, IRegPointer, REG} from "lib/commons/registry";
import {IWspUiEnv, Wsp, WSP} from "lib/wsp/wsp";
import {SRC} from "lib/wsp/src";
import {SrcAction} from "back/wsp/actions/srcActions";
import {IShortDescCtx} from "lib/wsp/item";
import {Desk} from "lib/commons/desk";
import {IChainEnv} from "lib/wsp/chain";
import {IPubCtx} from "lib/wsp/wspMetaUi";
import {GenAction} from "back/wsp/views/gen/genActions";
import {IControlLabelContext, LabelAsyncArea} from "lib/commons/areas";
import {DOM, JSX} from "lib/commons/xml/dom";
import {WspArea} from "back/wsp/dialogs/wspPropsAreas";
import {BaseElement, FileSystemPath, MsgLabel, OMsgLabelInit} from "back/commons/basis";
import {MxFormElement, OFormElementInit} from "lib/commons/forms";
import {Button} from "back/commons/widgets/buttons";
import {DOMSH} from "lib/commons/xml/domsh";
import {IO} from "lib/commons/io/io";
import {ERROR} from "lib/core/errorReport";
import {POPUP} from "back/commons/widgets/popups";

function refUriFromShortDescCtx(wsp: Wsp, ctx: IShortDescCtx): string {
	let shortDesc = ctx.shortDescs[0];
	if (shortDesc) {
		const itemType = wsp.wspMetaUi.getItemType(shortDesc.itModel);
		return itemType.getMainStreamSrcUri(shortDesc) || shortDesc.srcUri;
	} else {
		return SRC.URI_ROOT;
	}
}
const externalEditor = new SrcAction<IShortDescCtx>()
	.setLabel("Ouvrir dans l'éditeur par défaut du système")
	.setVisible((ctx) => {
		const {env} = ctx.reg;
		return Desk.electron && env.universe.config.local && env.wsp.backEnd == 'fs';
	})
	.setGroup("explorer")
	.setExecute(async function (this: SrcAction<IShortDescCtx>, ctx) {
		const {env} = ctx.reg;
		const srcFilePath = await WSP.fetchFilePath(env.wsp, env.uiRoot, refUriFromShortDescCtx(env.wsp, ctx));
		window.postMessage({type: "client:modules:localServer:open", path: srcFilePath}, location.origin);
	});


const revealInFs = new SrcAction<IShortDescCtx>()
	.setLabel("Révéler dans le système de fichiers")
	.setVisible((ctx) => {
		const {env} = ctx.reg;
		return Desk.electron && env.universe.config.local && env.wsp.backEnd == 'fs';
	})
	.setGroup("explorer")
	.setExecute(async function (this: SrcAction<IShortDescCtx>, ctx) {
		const {env} = ctx.reg;
		const srcFilePath = await WSP.fetchFilePath(env.wsp, env.uiRoot, refUriFromShortDescCtx(env.wsp, ctx));
		window.postMessage({type: "client:modules:localServer:reveal", path: srcFilePath}, location.origin);
	});

const copyLocalPath = new SrcAction<IShortDescCtx>()
	.setLabel("Copier le chemin du système de fichiers")
	.setVisible((ctx) => {
		const {env} = ctx.reg;
		return env.universe.config.local && env.wsp.backEnd == 'fs';
	})
	.setGroup("clipboard")
	.setExecute(async function (this: SrcAction<IShortDescCtx>, ctx) {
		const {env} = ctx.reg;
		const srcFilePath = await WSP.fetchFilePath(env.wsp, env.uiRoot, refUriFromShortDescCtx(env.wsp, ctx));
		await navigator.clipboard.writeText(srcFilePath);
		POPUP.showNotifInfo("Le chemin du fichier a été copié dans le presse-papier.", ctx.emitter);
	});

/** Gen : action "révéler */
class RevealGenAction extends GenAction {

	constructor(id?: string) {
		super(id || "reveal");
		this._label = "Révéler";
		this._icon = "/@skin@/wsp/views/generators/reveal.svg";
		this._description = Desk.electron ? "Révéler dans le système de fichier" : "Copier le chemin du répertoire de génération dans le presse-papier";
		this._hideForStatuses = "null";//failed,
	}

	isVisible(ctx: IPubCtx): boolean {
		return ctx.genInfo.localPathPub && ctx.reg.env.universe.config.local && super.isVisible(ctx);
	}

	execute(ctx: IPubCtx, ev?: Event): any | 'noPreventDefault' {
		if (Desk.electron)
			window.postMessage({type: "client:modules:localServer:reveal", path: ctx.genInfo.localPathPub}, location.origin);
		else{
			navigator.clipboard.writeText(ctx.genInfo.localPathPub);
			POPUP.showNotifInfo("Le chemin de génération a été copié dans le presse-papier.", ctx.uiContext)
		}
	}
}

/**
 * WSP FS create : specif du répertoire des contenus de l'atelier
 */

class WspFolderContentInputArea extends LabelAsyncArea<IControlLabelContext & IRegPointer<IChainEnv>> {

	constructor() {
		super("folderContent");
		this.setLabel("Contenus");
		this.setDescription("Répertoire des contenus de l'atelier");
	}

	isVisible(ctx: IControlLabelContext & IRegPointer<IChainEnv>): boolean {
		return ctx.reg.env.universe.config.local && ctx.reg.env.universe.config.backEnd == 'fs' && super.isVisible(ctx);
	}

	async _loadControl(ctx: IControlLabelContext & IRegPointer<IChainEnv>, name: string): Promise<HTMLElement> {
		const infoWspProvider = await WSP.fetchInfoWspProvider(ctx.reg.env.universe.wspServer);
		const inputFile = <InputLocalPath î={{
			name,
			directory: true,
			canEditPath: null,
			defaultPath: infoWspProvider.defaultContentPath,
		} as OInputLocalPath}/> as InputLocalPath;
		inputFile.inputElt.placeholder = "Chemin par défaut";
		inputFile.inputElt.title = infoWspProvider.defaultContentPath;
		return inputFile;
	}

}

/** WSP FS : affichage du répertoire des contenus de l'atelier */
class WspFolderContentArea extends WspArea {
	protected _infoWspField: 'content' | 'gen';

	constructor(id = 'content', protected asSubField = false) {
		super(id);
		this.setLabel("Contenus");
		this.setDescription("Répertoire des contenus de l'atelier");
		this._infoWspField = 'content';
	}

	isVisible(ctx: IRegPointer<IWspUiEnv>): boolean {
		return ctx.reg.env.universe.config.local && ctx.reg.env.wsp.backEnd == 'fs' && super.isVisible(ctx);
	}

	buildBody(ctx: IControlLabelContext & IRegPointer<IWspUiEnv>): HTMLElement {
		const areaCtx: IControlLabelContext & IRegPointer<IWspUiEnv> = Object.assign({}, ctx, {buildControlLabel: !this.asSubField});
		const elt = super.buildBody(areaCtx);
		if (this.asSubField) {
			DOM.addClass(elt, "allCols");
			elt.style.marginInlineStart = '2em';
		}
		return elt;
	}

	_buildControl(ctx: IControlLabelContext & IRegPointer<IWspUiEnv>, name: string): HTMLElement {
		let infoWsp = ctx.reg.env.wsp ? (ctx.reg.env.wsp.infoWsp || ctx.reg.env.wsp.infoWspError) : null;
		if (infoWsp && infoWsp[this._infoWspField]) return <FileSystemPath î={{
			label: infoWsp[this._infoWspField],
			ellipsis: !this.asSubField,
			type: "dir",
		}} class={this.asSubField ? 'small' : null}/>
		else
			return <MsgLabel î={{label: "Information non disponible", level: "info"} as OMsgLabelInit}/> as MsgLabel;
	}

}

class WspFolderGenArea extends WspFolderContentArea {

	constructor(id = 'gen', protected asSubField = false) {
		super(id, asSubField);
		this.setLabel("Générations");
		this.setDescription("Répertoire de génération de l'atelier");
		this._infoWspField = 'gen';
	}

	isVisible(ctx: IRegPointer<IWspUiEnv>): boolean {
		return ctx.reg.env.universe.config.local && super.isVisible(ctx);
	}
}


/**
 * Init du registre spécifique au univers chain "local"
 * 		init du registre **métier** de l'univers
 */
export function initLocalServerReg(reg: IReg<IChainEnv>) {
	/** srcNode - explorer */
	if(Desk.electron) {
		reg.addToList("actions:wsp:shortDesc", "externalEditor", REG.LEVELAUTH_CLIENT, externalEditor, 100);
		reg.addToList("actions:itemFolder:shortDesc", "externalEditor", REG.LEVELAUTH_CLIENT, externalEditor, 100);
		reg.addToList("actions:wsp:shortDesc", "revealInFs", REG.LEVELAUTH_CLIENT, revealInFs, 100);
		reg.addToList("actions:itemFolder:shortDesc", "revealInFs", REG.LEVELAUTH_CLIENT, revealInFs, 100);
	}
	reg.addToList("actions:wsp:shortDesc", "copyLocalPath", REG.LEVELAUTH_CLIENT, copyLocalPath, 100);
	reg.addToList("actions:itemFolder:shortDesc", "copyLocalPath", REG.LEVELAUTH_CLIENT, copyLocalPath, 100);


	/** Gen */
	reg.addToList("actions:gen", "reveal", 1, new RevealGenAction(), 30);

	/** Wsps */
	REG.reg.addToList('wspProps:create:fs', 'localPaths', 1, new WspFolderContentInputArea(), 21);
	REG.reg.addToList('wspProps:edit:fs:infos', 'folderContent', 1, new WspFolderContentArea(), 21);
	REG.reg.addToList('wspProps:edit:fs:infos', 'folderGen', 1, new WspFolderGenArea(), 22);

	REG.reg.addToList('wspProps:remove:fs', 'folderContent', 1, new WspFolderContentArea(null, true), 12);
	REG.reg.addToList('wspProps:remove:commons', 'folderGen', 1, new WspFolderGenArea(null, true), 22);

}

/**
 * Input pour retourner un chemin local en interrogeant le client Electron
 */
export interface OInputLocalPath extends OFormElementInit {
	/** Sélection de répertoire au lieu de fichier */
	directory?: boolean;
	/** Sélection multiple */
	multiple?: boolean;
	/** Extension autorisée  {@see https://www.electronjs.org/docs/api/structures/file-filter}*/
	filters: { name: string, extensions: string[]}[];
	/** Chemin de base utilisé pour initialiser le widget de sélection en cas d'absence de value */
	defaultPath?: string;
	/** Edition libre du path dans la zone input :
	 * 		par défaut, false sur electron, true autrement
	 */
	canEditPath?: boolean|null;
}

export class InputLocalPath extends MxFormElement(BaseElement) {
	protected _params: OInputLocalPath;
	protected reg: IReg<any>;

	public inputElt: HTMLInputElement;

	protected _inputBtn?: Button;

	protected _value: string | string[];

	_initialize(init: OInputLocalPath) {
		this._params = init;

		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);
		this.inputElt = sr.appendChild(<input type="text" name="customInputPath" oninput={this.onInputChange}/>) as HTMLInputElement;
		DOM.setAttrBool(this.inputElt, "readonly", this._params.canEditPath==null ? Desk.electron : this._params.canEditPath);
		if(Desk.electron) this._inputBtn = sr.appendChild(<c-button ui-context="bar" icon={this._params.directory ? '/@skin@/commons/icons/choiceLocalDir.svg' : '/@skin@/commons/icons/choiceLocalFile.svg'}
																							title={this._params.directory ? 'Choisir un répertoire...' : 'Choisir un fichier...'} onclick={this.onSelectPath}/>) as Button;

		this.onInputChange.call(this.inputElt);
		this._initializeForm(init);
	}

	get value(): string | string[] {
		return this._value;
	}

	set value(path: string | string[]) {
		this._value = path;
	}

	get disabled(): boolean {
		return super.disabled;
	}

	set disabled(val) {
		this.inputElt.disabled = val;
		if(this._inputBtn) this._inputBtn.disabled = val;
		super.disabled = val;
	}

	async onSelectPath(this: HTMLButtonElement) {
		const self = DOMSH.findHost<InputLocalPath>(this);

		const selectReply = await IO.sendMessage(Object.assign({ type: "client:modules:localServer:selectPath" }, self._params), location.origin, 0);
		if (selectReply.type == 'error') ERROR.log(selectReply.msg);
		else {
			if (!selectReply.paths) return;

			else if (selectReply.paths.length == 1) self._value = selectReply.paths[0];
			else self._value = selectReply.paths;

			self._refresh();
		}
	}

	onInputChange(this: HTMLInputElement) {
		const self = DOMSH.findHost<InputLocalPath>(this);

		if (!this.value) self._value = null;
		else if (self._params.multiple) self._value = this.value.split(',');
		else self._value = this.value;
	}

	protected _refresh() {
		if (!this._value) this.inputElt.value = "";
		else if (Array.isArray(this._value)) this.inputElt.value = this._value.join(',');
		else this.inputElt.value = this._value;
	}

	extractJson(parent: Dict<any>): boolean {
		if (!this.name) return false;
		this.value = parent[this.name];
	}

	fillJson(parent: any, root: any): void {
		if (!this.name || this.indeterminate) return;
		parent[this.name] = this.value;
	}
}

REG.reg.registerSkin("c-input-local-path", 1, /* language=CSS */`
	:host {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: row;
	}
		
	input[type=text] {
		flex:1;
		min-width: 0;
	  background-color: var(--form-bgcolor);
	  color: var(--form-color);
	  border: 1px solid var(--border-color);
	  text-overflow: ellipsis;
		margin: 0 .5em 0 0;
	}
	
	input:disabled {
	  background-color: transparent;
  }

  input:invalid{
	  border-color: var(--error-color);
  }

  input[readonly] {
	  background-color: transparent;
	  border: none;
	  cursor: auto;
  }
`);

customElements.define("c-input-local-path", InputLocalPath);